import type { AmountDisplayUnit } from '../../lib/finance-display.js';

/** Compact money parts for a KPI tile: «۱٫۹۴» + «میلیارد». */
export interface CompactAmountParts {
  readonly value: string;
  readonly scale: 'میلیارد' | 'میلیون';
}

const MILLION = 1_000_000n;
const BILLION = 1_000_000_000n;

/**
 * Compacts an integer-Rial digit string for a KPI tile the way the v2 Finance
 * frame does («۱٫۹۴ میلیارد» / «۸۶۰ میلیون»). Pure BigInt end to end — the
 * amount is never passed through `Number`, so a large balance cannot lose
 * precision (AGENTS.md §8). The display unit (ریال/تومان) only decides
 * whether the Rial value is divided by 10 first; the stored value stays
 * integer Rial.
 *
 * Returns `null` below one million (display unit): the caller then prints the
 * full grouped number, which is exact at that magnitude. Truncates (never
 * rounds up) so a tile never shows more than the ledger holds.
 */
export function compactAmountParts(
  rialDigits: string,
  unit: AmountDisplayUnit,
): CompactAmountParts | null {
  let raw: bigint;
  try {
    raw = BigInt(rialDigits);
  } catch {
    return null;
  }
  const negative = raw < 0n;
  let abs = negative ? -raw : raw;
  if (unit === 'toman') abs = abs / 10n;
  const divisor = abs >= BILLION ? BILLION : abs >= MILLION ? MILLION : null;
  if (divisor === null) return null;
  const whole = abs / divisor;
  const hundredths = ((abs % divisor) * 100n) / divisor;
  let text = whole.toLocaleString('fa-IR');
  if (whole < 100n && hundredths > 0n) {
    const frac = hundredths.toString().padStart(2, '0').replace(/0$/, '');
    text += `٫${frac.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.charAt(Number(d)))}`;
  }
  return {
    value: `${negative ? '-' : ''}${text}`,
    scale: divisor === BILLION ? 'میلیارد' : 'میلیون',
  };
}

/** Integer percentage `part / total` (floor) with BigInt — `null` when total is not positive. */
export function percentOfRial(partDigits: bigint, totalDigits: bigint): number | null {
  if (totalDigits <= 0n) return null;
  const pct = (partDigits * 100n) / totalDigits;
  return Number(pct > 100n ? 100n : pct < 0n ? 0n : pct);
}
